/*
 * file-list 文件列表块
 *
 * Schema: { title?, files: [{ name, url, size?, type?, desc? }] }
 *
 * 每个 file 渲染成 ExportPanel + ShareTargets。"保存模板"硬入口已下线 —
 * Agent 在填表完成的合适时机自决主动提议(一会话一次), 不再在 file bundle
 * 里硬挂入口.
 *
 * 子卡回调在本地处理:
 *   - ExportPanel: 直接下载该 url(formats 选择器视觉保留, 主按钮即下载)
 *   - ShareTargets: 内部已自洽(剪贴板 / mailto / Modal)
 */
import React, { useCallback } from 'react'
import { tryParseJson, FileListData, FileItem } from './types'
import { useInvalidBlockWarning } from './useInvalidBlockWarning'
import { ExportPanel } from '../attachments/ExportPanel'
import { ShareTargets } from '../attachments/ShareTargets'
import { downloadFile } from '../attachments/download'
import { FilePreviewCard } from './FilePreviewCard'
import './FileListBlock.scss'

interface FileListBlockProps {
  code: string
}

/** 按扩展名给出"导出 XX 文档"的按钮文案 */
function exportButtonText(ext: string): string {
  switch (ext) {
    case 'xlsx':
    case 'xls':
    case 'csv':
      return '导出 Excel 表格'
    case 'pdf':
      return '导出 PDF 文档'
    case 'docx':
    case 'doc':
      return '导出 Word 文档'
    case 'pptx':
    case 'ppt':
      return '导出 PPT 文档'
    default:
      return '导出文档'
  }
}

const DEFAULT_SHARE_TARGETS = [
  { key: 'link', label: '复制链接', emoji: '🔗', tone: 'neutral' as const },
  { key: 'email', label: '邮件', emoji: '✉️', tone: 'blue' as const },
  { key: 'wechat', label: '微信', emoji: '💬', tone: 'green' as const },
  { key: 'dingtalk', label: '钉钉', emoji: '🔔', tone: 'orange' as const }
]

const FileBundle: React.FC<{ file: FileItem }> = ({ file }) => {
  const ext = (file.name.split('.').pop() || '').toLowerCase()

  const handleExport = useCallback(
    (_format: string) => {
      // 格式选择当前不做服务端转换，直接下文件本身；后端支持转换后再扩
      downloadFile(file.url, file.name)
    },
    [file.url, file.name]
  )

  // "保存模板"卡片已下线 — 由 Agent 在填表完成的合适时机自决主动提议
  // (一会话一次), 不再在 file bundle 里硬挂入口.

  return (
    <div className="aiClerk-block-fileList__bundle">
      <ExportPanel
        data={{
          fileName: file.name,
          fileSize: file.size,
          buttonText: exportButtonText(ext)
        }}
        onExport={handleExport}
      />
      <ShareTargets targets={DEFAULT_SHARE_TARGETS} shareUrl={file.url} />
    </div>
  )
}

function canRenderXlsxPreview(file: FileItem): boolean {
  if (!file.preview_url) return false
  return (
    /\.xlsx(?:[?#]|$)/i.test(file.preview_url) || /\.xlsx$/i.test(file.name)
  )
}

export const FileListBlock: React.FC<FileListBlockProps> = ({ code }) => {
  const data = tryParseJson<FileListData>(code)

  // 流式中 JSON 还没收齐时 tryParseJson 会返回 null，silent 不渲染占位，
  // 避免气泡里闪一下"📎 文件列表数据无效"再切走。流结束后 JSON 完整自然渲染；
  // 流式结束后仍无效 → useInvalidBlockWarning 会 console.warn 留排查线索。
  const valid = !!data && Array.isArray(data.files) && data.files.length > 0
  useInvalidBlockWarning('file-list', code, valid)
  if (!data || !Array.isArray(data.files) || data.files.length === 0) {
    return null
  }

  return (
    <div className="aiClerk-block-fileList">
      {data.title && (
        <div className="aiClerk-block-fileList__title">{data.title}</div>
      )}
      <div className="aiClerk-block-fileList__list">
        {data.files.map((f, i) =>
          canRenderXlsxPreview(f) ? (
            <FilePreviewCard key={`${f.url}-${i}`} file={f} />
          ) : (
            <FileBundle key={`${f.url}-${i}`} file={f} />
          )
        )}
      </div>
    </div>
  )
}
